import { useCallback, useMemo } from 'react';
import type { FocusEventHandler, ReactNode } from 'react';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import type { IdOption } from '../IdAutocomplete';

// Native Select values are always strings in this codebase (Mongo-style ids),
// unlike `IdAutocomplete`'s TId which stays generic over React's full `Key`.
interface Props<TId extends string, TOption extends IdOption<TId>> {
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly fullWidth?: boolean;
  readonly helperText?: ReactNode;
  readonly id: string;
  readonly label: string;
  readonly name?: string;
  readonly nullable?: boolean;
  readonly onBlur?: FocusEventHandler<HTMLElement>;
  readonly onChange: (ids: readonly TId[]) => void;
  readonly options: readonly TOption[];
  readonly required?: boolean;
  readonly sx?: SxProps<Theme>;
  readonly value: readonly TId[];
}

type SelectableValue<TId extends string> = TId | typeof EMPTY_STRING;

const isRealId = <TId extends string>(
  value: SelectableValue<TId>,
): value is TId => value !== EMPTY_STRING;

const resolveNextSelection = <TId extends string>(
  rawValues: readonly SelectableValue<TId>[],
  wasEmptySelection: boolean,
): readonly TId[] => {
  if (!rawValues.includes(EMPTY_STRING)) {
    return rawValues.filter(isRealId<TId>);
  }

  if (wasEmptySelection) {
    return rawValues.filter(isRealId<TId>);
  }

  return EMPTY_ARRAY;
};

export const IdMultiSelect = <
  TId extends string,
  TOption extends IdOption<TId>,
>({
  disabled = false,
  error = false,
  fullWidth = true,
  helperText,
  id,
  label,
  name,
  nullable = false,
  onBlur,
  onChange,
  options,
  required = false,
  sx,
  value,
}: Readonly<Props<TId, TOption>>) => {
  const optionById = useMemo(
    () => new Map(options.map((option) => [option.id, option])),
    [options],
  );

  const isEmptySelection = value.length === 0;

  const handleChange = useCallback(
    (event: Readonly<SelectChangeEvent<readonly SelectableValue<TId>[]>>) => {
      const rawValues = event.target.value as readonly SelectableValue<TId>[];
      onChange(resolveNextSelection(rawValues, isEmptySelection));
    },
    [isEmptySelection, onChange],
  );

  const renderValue = useCallback(
    (selected: readonly SelectableValue<TId>[]) =>
      selected
        .filter(isRealId<TId>)
        .map((selectedId) => optionById.get(selectedId)?.label ?? selectedId)
        .join(', '),
    [optionById],
  );

  const labelId = `${id}-label`;

  return (
    <FormControl
      disabled={disabled}
      error={error}
      fullWidth={fullWidth}
      required={required}
      sx={sx}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<readonly SelectableValue<TId>[]>
        id={id}
        label={label}
        labelId={labelId}
        multiple
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        renderValue={renderValue}
        required={required}
        value={value}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!nullable && (
          <MenuItem value={EMPTY_STRING}>
            <em>Laisser vide</em>
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
